import React, { useEffect, useState, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { API, useApp } from "@/contexts/AppContext";
import { Button } from "@/components/ui/button";
import { HeartPulse, Printer, ArrowLeft, Paperclip } from "lucide-react";

const CAT_LABEL = { pregnant: "Femme enceinte", child: "Enfant 0-3 ans", elderly: "Personne âgée", general: "Général" };
const LANG_LABEL = { fr: "Français", ln: "Lingala", sw: "Swahili", ts: "Tshiluba", kg: "Kikongo" };
const STATUS_LABEL = { external: "Externe", hospitalized: "Hospitalisé", deceased: "Décédé" };

export default function PatientPrint() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useApp();
  const [p, setP] = useState(null);
  const [hospital, setHospital] = useState(null);
  const [err, setErr] = useState("");
  const printedOnce = useRef(false);

  useEffect(() => {
    (async () => {
      try {
        const [pr, hr] = await Promise.all([
          axios.get(`${API}/patients/${id}`),
          axios.get(`${API}/hospitals/me`).catch(() => ({ data: null })),
        ]);
        setP(pr.data);
        setHospital(hr.data);
      } catch (e) {
        setErr(e.response?.data?.detail || "Erreur de chargement");
      }
    })();
  }, [id]);

  const fmtDate = (s) => (s ? new Date(s).toLocaleString("fr-FR", { dateStyle: "medium", timeStyle: "short" }) : "—");
  const fmtDateShort = (s) => (s ? new Date(s).toLocaleDateString("fr-FR") : "—");

  const addressLine = () => {
    if (!p) return "";
    const struct = [p.avenue, p.quartier, p.commune, p.ville, p.pays].filter(Boolean).join(" · ");
    return struct || p.address || "—";
  };

  if (err) return <div className="p-10 text-center text-[#C62828]">{err}</div>;
  if (!p) return <div className="p-10 text-center text-[#5C6B61]">Chargement…</div>;

  return (
    <div className="min-h-screen bg-white text-[#1A1D1A] print:bg-white" data-testid="patient-print-page">
      {/* Toolbar - hidden in print */}
      <div className="print:hidden border-b border-[#E5EBE7] px-6 py-3 flex items-center justify-between sticky top-0 bg-white/95 backdrop-blur z-10">
        <Button variant="outline" size="sm" onClick={() => navigate(-1)} data-testid="print-back">
          <ArrowLeft className="w-4 h-4 mr-1.5"/> Retour
        </Button>
        <div className="text-xs text-[#5C6B61]">Aperçu impression · Format A4</div>
        <Button onClick={() => window.print()} className="bg-[#1E4631] hover:bg-[#163424]" size="sm" data-testid="print-btn">
          <Printer className="w-4 h-4 mr-1.5"/> Imprimer
        </Button>
      </div>

      {/* Printable page */}
      <div className="max-w-[210mm] mx-auto p-10 print:p-8 print:max-w-none">
        {/* Header */}
        <div className="flex items-start justify-between pb-5 border-b-2 border-[#1E4631]">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-xl bg-[#1E4631] grid place-items-center">
              <HeartPulse className="w-6 h-6 text-white"/>
            </div>
            <div>
              <div className="font-[Outfit] font-bold text-xl leading-tight">KWIKILA Care</div>
              <div className="text-xs text-[#5C6B61]">La santé en confiance · RDC</div>
            </div>
          </div>
          <div className="text-right text-xs text-[#5C6B61]">
            {hospital && <div className="font-semibold text-[#1A1D1A] text-sm">{hospital.name}</div>}
            {hospital?.city && <div>{hospital.city}{hospital.pays ? ` · ${hospital.pays}` : ""}</div>}
            <div className="mt-1">Édité le {new Date().toLocaleString("fr-FR")}</div>
            <div>Par {user?.name || user?.email}</div>
          </div>
        </div>

        {/* Title */}
        <div className="mt-6 mb-4">
          <div className="text-xs uppercase tracking-widest text-[#5C6B61]">Fiche patient</div>
          <h1 className="font-[Outfit] font-bold text-3xl mt-1">{p.name}</h1>
          <div className="flex flex-wrap gap-2 mt-2 text-xs">
            <span className="px-2 py-0.5 rounded-full bg-[#1E4631]/10 text-[#1E4631] font-medium">{CAT_LABEL[p.category] || p.category}</span>
            <span className="px-2 py-0.5 rounded-full bg-[#FDFBF7] border border-[#E5EBE7]">{LANG_LABEL[p.language] || p.language}</span>
            <span className={`px-2 py-0.5 rounded-full font-medium ${p.status === "hospitalized" ? "bg-[#D96C4A]/15 text-[#D96C4A]" : p.status === "deceased" ? "bg-[#C62828]/15 text-[#C62828]" : "bg-[#2E7D32]/10 text-[#2E7D32]"}`}>
              {STATUS_LABEL[p.status] || "Externe"}
            </span>
          </div>
        </div>

        {/* Identity grid */}
        <section className="mt-6">
          <h2 className="font-[Outfit] font-semibold text-sm uppercase tracking-wider text-[#1E4631] mb-2">Identité & contact</h2>
          <div className="grid grid-cols-2 gap-y-2 gap-x-8 text-sm border border-[#E5EBE7] rounded-lg p-4">
            <Field label="Nom complet" value={p.name}/>
            <Field label="Âge" value={p.age != null ? `${p.age} ans` : "—"}/>
            <Field label="Téléphone" value={p.phone}/>
            <Field label="Langue préférée" value={LANG_LABEL[p.language] || p.language}/>
            <Field label="Catégorie" value={CAT_LABEL[p.category] || p.category}/>
            <Field label="Identifiant" value={p.id?.slice(0, 8) + "…"}/>
            <div className="col-span-2"><Field label="Adresse" value={addressLine()}/></div>
            {p.notes && <div className="col-span-2"><Field label="Notes d'accueil" value={p.notes}/></div>}
          </div>
        </section>

        {/* Hospitalization */}
        {p.status === "hospitalized" && (
          <section className="mt-6">
            <h2 className="font-[Outfit] font-semibold text-sm uppercase tracking-wider text-[#D96C4A] mb-2">Hospitalisation en cours</h2>
            <div className="grid grid-cols-2 gap-y-2 gap-x-8 text-sm border border-[#D96C4A]/40 bg-[#D96C4A]/5 rounded-lg p-4">
              <Field label="Chambre" value={p.room_number || "—"}/>
              <Field label="Admis le" value={fmtDate(p.admission_date)}/>
              <div className="col-span-2"><Field label="Motif d'admission" value={p.admission_reason || "—"}/></div>
            </div>
          </section>
        )}

        {/* Death */}
        {p.status === "deceased" && (
          <section className="mt-6">
            <h2 className="font-[Outfit] font-semibold text-sm uppercase tracking-wider text-[#C62828] mb-2">Registre décès</h2>
            <div className="grid grid-cols-2 gap-y-2 gap-x-8 text-sm border border-[#C62828]/40 bg-[#C62828]/5 rounded-lg p-4">
              <Field label="Date du décès" value={fmtDate(p.deceased_at)}/>
              <Field label="Cause" value={p.death_cause || "—"}/>
              {p.death_notes && <div className="col-span-2"><Field label="Notes" value={p.death_notes}/></div>}
            </div>
          </section>
        )}

        {/* Medical notes */}
        <section className="mt-6">
          <h2 className="font-[Outfit] font-semibold text-sm uppercase tracking-wider text-[#1E4631] mb-2">Notes médicales</h2>
          {p.medical_notes?.length ? (
            <div className="space-y-3">
              {p.medical_notes.map((n) => (
                <div key={n.id} className="border border-[#E5EBE7] rounded-lg p-3 text-sm break-inside-avoid">
                  <div className="text-xs text-[#5C6B61] mb-1">
                    <strong className="text-[#1A1D1A]">{n.by}</strong> · {fmtDate(n.at)}
                  </div>
                  <div className="whitespace-pre-wrap">{n.content}</div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-sm text-[#5C6B61] italic">Aucune note médicale enregistrée.</div>
          )}
        </section>

        {/* Attachments */}
        {p.attachments?.length > 0 && (
          <section className="mt-6">
            <h2 className="font-[Outfit] font-semibold text-sm uppercase tracking-wider text-[#1E4631] mb-2 inline-flex items-center gap-1.5">
              <Paperclip className="w-4 h-4"/> Pièces jointes
            </h2>
            <ul className="border border-[#E5EBE7] rounded-lg divide-y divide-[#E5EBE7]">
              {p.attachments.map((a) => (
                <li key={a.id} className="p-3 text-sm flex items-center justify-between gap-3">
                  <div className="min-w-0">
                    <div className="font-medium truncate">{a.filename}</div>
                    <div className="text-xs text-[#5C6B61]">
                      {(a.content_type || "").split("/")[1] || "fichier"} · {Math.round((a.size || 0) / 1024)} Ko · déposé le {fmtDateShort(a.uploaded_at)} par {a.uploaded_by}
                    </div>
                    {a.description && <div className="text-xs text-[#5C6B61] italic mt-0.5">{a.description}</div>}
                  </div>
                </li>
              ))}
            </ul>
          </section>
        )}

        {/* Footer */}
        <div className="mt-10 pt-4 border-t border-[#E5EBE7] text-[10px] text-[#5C6B61] flex justify-between">
          <div>KWIKILA Care · Fiche patient confidentielle · Ne pas diffuser sans consentement.</div>
          <div>Page 1</div>
        </div>
      </div>

      {/* Print CSS */}
      <style>{`
        @media print {
          @page { size: A4; margin: 14mm; }
          body { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
        }
      `}</style>
    </div>
  );
}

function Field({ label, value }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-widest text-[#5C6B61] mb-0.5">{label}</div>
      <div className="text-sm">{value || "—"}</div>
    </div>
  );
}
